import { useEffect } from "react";

const SITE_ORIGIN = (
    process.env.REACT_APP_SITE_ORIGIN || "https://sleepcomfortreport.com"
).replace(/\/$/, "");

const upsertMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
            if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
    }
    if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
};

const upsertLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const injectJsonLd = (id, data) => {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.text = JSON.stringify(data);
};

const cleanupJsonLd = (ids) => {
    ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.parentNode) el.parentNode.removeChild(el);
    });
};

export const useSEO = ({
    title,
    description,
    path,
    ogImage,
    schemas = [],
}) => {
    useEffect(() => {
        const canonical = `${SITE_ORIGIN}${path}`;
        document.title = title;

        upsertMeta('meta[name="description"]', {
            name: "description",
            content: description,
        });

        // Canonical
        upsertLink("canonical", canonical);

        // Open Graph
        upsertMeta('meta[property="og:type"]', {
            property: "og:type",
            content: "article",
        });
        upsertMeta('meta[property="og:title"]', {
            property: "og:title",
            content: title,
        });
        upsertMeta('meta[property="og:description"]', {
            property: "og:description",
            content: description,
        });
        upsertMeta('meta[property="og:url"]', {
            property: "og:url",
            content: canonical,
        });
        upsertMeta('meta[property="og:site_name"]', {
            property: "og:site_name",
            content: "Better Rest Report",
        });
        if (ogImage) {
            upsertMeta('meta[property="og:image"]', {
                property: "og:image",
                content: ogImage,
            });
        }

        // Twitter
        upsertMeta('meta[name="twitter:card"]', {
            name: "twitter:card",
            content: "summary_large_image",
        });
        upsertMeta('meta[name="twitter:title"]', {
            name: "twitter:title",
            content: title,
        });
        upsertMeta('meta[name="twitter:description"]', {
            name: "twitter:description",
            content: description,
        });
        if (ogImage) {
            upsertMeta('meta[name="twitter:image"]', {
                name: "twitter:image",
                content: ogImage,
            });
        }

        // Robots — legal pages opt-out via path prefix
        const legalPaths = ["/privacy", "/terms", "/contact", "/disclosure"];
        const robots = legalPaths.includes(path)
            ? "noindex,follow"
            : "index,follow,max-image-preview:large";
        upsertMeta('meta[name="robots"]', {
            name: "robots",
            content: robots,
        });

        // JSON-LD schemas
        const ids = schemas.map((_, i) => `ld-${path.replace(/\W+/g, "_")}-${i}`);
        schemas.forEach((schema, i) => injectJsonLd(ids[i], schema));

        return () => cleanupJsonLd(ids);
    }, [title, description, path, ogImage, JSON.stringify(schemas)]);
};

export const SITE = {
    origin: SITE_ORIGIN,
    name: "Better Rest Report",
};
